import { Label } from "@radix-ui/react-label";
import { useEffect, useId, useMemo, useRef, type ComponentProps } from "react";
import {
    get,
    useFormContext,
    type FieldValues,
    type Path,
    type PathValue,
} from "react-hook-form";
import { cn } from "@/lib/utils";
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
} from "@/components/ui/select";
import RequiredMark from "./RequiredMark";
import { useSchema } from "@/v2/hooks/SchemaContext";
import { ZodArray } from "zod";

type SelectFormFieldBaseProps = Omit<
    ComponentProps<typeof Select>,
    "name" | "onValueChange" | "value" | "defaultValue"
>;

interface ArSelectFormFieldProps<T extends FieldValues>
    extends SelectFormFieldBaseProps {
    name: Path<T>;
    label: string;
    options: { label: string; value: PathValue<T, Path<T>> }[];
    defaultValue?: string | Record<string, string>;
}

export function ArSelectFormField<T extends FieldValues>({
    label,
    name,
    options,
    disabled,
    defaultValue,
    ...rest
}: ArSelectFormFieldProps<T>) {
    const {
        setValue,
        watch,
        formState: { errors },
    } = useFormContext<T>();

    const defaultSetRef = useRef(false);
    const error = get(errors, name);
    const id = useId();
    const value = watch(name);

    const schema = useSchema();
    const isRequired = useMemo(() => {
        const parts = name.split(".");
        let currentSchema: any = schema;

        for (const part of parts) {
            if (!currentSchema?.shape) return false;

            // Handle array index (numeric parts)
            if (/^\d+$/.test(part)) {
                if (currentSchema instanceof ZodArray) {
                    currentSchema = currentSchema.element;
                }
                continue;
            }

            currentSchema = currentSchema.shape[part];

            if (currentSchema instanceof ZodArray) {
                currentSchema = currentSchema.element;
            }
        }

        const testResult = currentSchema?.safeParse("");
        return !testResult?.success;
    }, [schema, name]);

    useEffect(() => {
        if (defaultValue && !value && !defaultSetRef.current)
            setValue(name, defaultValue as PathValue<T, Path<T>>);
        defaultSetRef.current = true;
    }, [defaultValue, value]);

    return (
        <div>
            <Label
                htmlFor={id}
                className={cn(
                    "text-sm font-medium",
                    error && "text-destructive",
                )}
            >
                {label}
                {isRequired && <RequiredMark />}
            </Label>
            <Select
                value={(value ?? "") as PathValue<T, Path<T>>}
                onValueChange={(val) =>
                    setValue(name, val as PathValue<T, Path<T>>)
                }
                disabled={disabled}
                {...rest}
            >
                <SelectTrigger
                    id={id}
                    className={cn(
                        error &&
                            "border-destructive focus-visible:ring-destructive",
                    )}
                >
                    <SelectValue placeholder={`Select ${label}`} />
                </SelectTrigger>

                <SelectContent>
                    {options && options.length > 0 && (
                        <>
                            {options
                                .filter((opt) => opt.value !== "")
                                .map((opt) => (
                                    <SelectItem
                                        key={opt.value}
                                        value={opt.value}
                                    >
                                        {opt.label}
                                    </SelectItem>
                                ))}
                        </>
                    )}
                </SelectContent>
            </Select>
            {error && (
                <p id={`${id}-error`} className="mt-1 text-xs text-destructive">
                    {error.message as string}
                </p>
            )}
        </div>
    );
}
